import axiosInstance from "./axiosInstance";
import logger from "../utils/logger";
import { extractErrorMessage } from "../utils/errorHandler";

// ============ Enums ============

export enum CompanyRequestStatus {
    DRAFT = "DRAFT",
    ACTIVE = "ACTIVE",
    FILLED = "FILLED",
    CLOSED = "CLOSED",
}

export enum MatchStatus {
    SUGGESTED = "SUGGESTED",
    CONFIRMED = "CONFIRMED",
    REJECTED = "REJECTED",
}

export enum MatchScore {
    HIGH = "HIGH",
    MEDIUM = "MEDIUM",
    LOW = "LOW",
}

// ============ Types ============

export interface SkillRequirement {
    questionKey: string;
    optionKeys: string[];
}

export interface CompanyRequestRoleMatch {
    id: number;
    companyRequestRoleId: number;
    workerId: string;
    workerName?: string;
    score: MatchScore;
    reason?: string;
    status: MatchStatus;
    confirmedByRecruiterId?: number;
    confirmedAt?: string;
    assessmentRound: number;
    /** Set on SUGGESTED rows when this worker is CONFIRMED on another role with overlapping dates */
    committedElsewhereOnRequestId?: number | null;
    committedElsewhereOnRoleId?: number | null;
    createdAt: string;
    updatedAt: string;
}

export interface CompanyRequestRole {
    id: number;
    companyRequestId: number;
    professionKey: string;
    numberOfWorkersNeeded?: number;
    minYearsExperience?: number;
    requirementsDescription?: string;
    requiredSkills?: SkillRequirement[];
    preferredSkills?: SkillRequirement[];
    status: CompanyRequestStatus;
    /** Null when the role has never been assessed; otherwise the timestamp of the last matching pass. */
    lastAssessedAt?: string | null;
    confirmedCount?: number;
    matchCount?: number;
    createdAt: string;
    updatedAt: string;
}

export interface CompanyRequest {
    id: number;
    businessId?: string;
    businessName?: string;
    accountManagerId?: string;
    startDate?: string;
    endDate?: string;
    location?: string;
    requiresWorkPermit: boolean;
    hoursPerWeek?: string;
    createdByRecruiterId?: number;
    roles: CompanyRequestRole[];
    /** Server-derived from child roles roll-up. */
    derivedStatus: CompanyRequestStatus;
    createdAt: string;
    updatedAt: string;
}

export interface CreateCompanyRequestRolePayload {
    professionKey: string;
    numberOfWorkersNeeded?: number;
    minYearsExperience?: number;
    requirementsDescription?: string;
    requiredSkills?: SkillRequirement[];
    preferredSkills?: SkillRequirement[];
}

export interface UpdateCompanyRequestRolePayload extends Partial<CreateCompanyRequestRolePayload> {
    status?: CompanyRequestStatus;
}

export interface CreateCompanyRequestPayload {
    businessId?: string;
    businessName?: string;
    accountManagerId?: string;
    startDate?: string;
    endDate?: string;
    location?: string;
    requiresWorkPermit?: boolean;
    hoursPerWeek?: string;
    roles: CreateCompanyRequestRolePayload[];
}

export interface UpdateCompanyRequestPayload {
    businessId?: string;
    businessName?: string;
    accountManagerId?: string;
    startDate?: string;
    endDate?: string;
    location?: string;
    requiresWorkPermit?: boolean;
    hoursPerWeek?: string;
}

export interface Business {
    id: string;
    name: string;
    [key: string]: any;
}

// ============ Parent CRUD ============

export const createCompanyRequest = async (data: CreateCompanyRequestPayload): Promise<CompanyRequest> => {
    try {
        const response = await axiosInstance.post<CompanyRequest>("/api/company-requests", data);
        return response.data;
    } catch (error: unknown) {
        logger.error("Error creating company request:", extractErrorMessage(error));
        throw error;
    }
};

export const getCompanyRequests = async (filters?: {
    page?: number;
    limit?: number;
    businessId?: string;
    recruiterId?: number;
    accountManagerId?: string;
    status?: CompanyRequestStatus;
    professionKey?: string;
    startDateFrom?: string;
    startDateTo?: string;
    endDateFrom?: string;
    endDateTo?: string;
}): Promise<{ data: CompanyRequest[]; total: number }> => {
    try {
        const response = await axiosInstance.get<{ data: CompanyRequest[]; total: number }>("/api/company-requests", {
            params: filters,
        });
        return response.data;
    } catch (error: unknown) {
        logger.error("Error fetching company requests:", extractErrorMessage(error));
        throw error;
    }
};

export const getCompanyRequest = async (id: number): Promise<CompanyRequest> => {
    try {
        const response = await axiosInstance.get<CompanyRequest>(`/api/company-requests/${id}`);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error fetching company request ${id}:`, extractErrorMessage(error));
        throw error;
    }
};

export const updateCompanyRequest = async (id: number, data: UpdateCompanyRequestPayload): Promise<CompanyRequest> => {
    try {
        const response = await axiosInstance.patch<CompanyRequest>(`/api/company-requests/${id}`, data);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error updating company request ${id}:`, extractErrorMessage(error));
        throw error;
    }
};

export const deleteCompanyRequest = async (id: number): Promise<void> => {
    try {
        await axiosInstance.delete(`/api/company-requests/${id}`);
    } catch (error: unknown) {
        logger.error(`Error deleting company request ${id}:`, extractErrorMessage(error));
        throw error;
    }
};

// ============ Role CRUD ============

export const addRole = async (requestId: number, data: CreateCompanyRequestRolePayload): Promise<CompanyRequestRole> => {
    try {
        const response = await axiosInstance.post<CompanyRequestRole>(`/api/company-requests/${requestId}/roles`, data);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error adding role to request ${requestId}:`, extractErrorMessage(error));
        throw error;
    }
};

export const getRole = async (requestId: number, roleId: number): Promise<CompanyRequestRole> => {
    try {
        const response = await axiosInstance.get<CompanyRequestRole>(`/api/company-requests/${requestId}/roles/${roleId}`);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error fetching role ${roleId}:`, extractErrorMessage(error));
        throw error;
    }
};

export const updateRole = async (requestId: number, roleId: number, data: UpdateCompanyRequestRolePayload): Promise<CompanyRequestRole> => {
    try {
        const response = await axiosInstance.patch<CompanyRequestRole>(`/api/company-requests/${requestId}/roles/${roleId}`, data);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error updating role ${roleId}:`, extractErrorMessage(error));
        throw error;
    }
};

export const deleteRole = async (requestId: number, roleId: number): Promise<void> => {
    try {
        await axiosInstance.delete(`/api/company-requests/${requestId}/roles/${roleId}`);
    } catch (error: unknown) {
        logger.error(`Error deleting role ${roleId}:`, extractErrorMessage(error));
        throw error;
    }
};

// ============ Match Operations ============

export const triggerRoleMatching = async (requestId: number, roleId: number): Promise<CompanyRequestRoleMatch[]> => {
    try {
        const response = await axiosInstance.post<CompanyRequestRoleMatch[]>(`/api/company-requests/${requestId}/roles/${roleId}/match`);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error triggering matching for role ${roleId}:`, extractErrorMessage(error));
        throw error;
    }
};

export const getRoleMatches = async (requestId: number, roleId: number): Promise<CompanyRequestRoleMatch[]> => {
    try {
        const response = await axiosInstance.get<CompanyRequestRoleMatch[]>(`/api/company-requests/${requestId}/roles/${roleId}/matches`);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error fetching matches for role ${roleId}:`, extractErrorMessage(error));
        throw error;
    }
};

export const confirmRoleMatch = async (requestId: number, roleId: number, matchId: number): Promise<CompanyRequestRoleMatch> => {
    try {
        const response = await axiosInstance.patch<CompanyRequestRoleMatch>(
            `/api/company-requests/${requestId}/roles/${roleId}/matches/${matchId}/confirm`,
        );
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error confirming match ${matchId}:`, extractErrorMessage(error));
        throw error;
    }
};

export const rejectRoleMatch = async (requestId: number, roleId: number, matchId: number): Promise<CompanyRequestRoleMatch> => {
    try {
        const response = await axiosInstance.patch<CompanyRequestRoleMatch>(
            `/api/company-requests/${requestId}/roles/${roleId}/matches/${matchId}/reject`,
        );
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error rejecting match ${matchId}:`, extractErrorMessage(error));
        throw error;
    }
};

// ============ Misc ============

export const getBusinesses = async (filters?: { search?: string; page?: number; limit?: number }): Promise<Business[]> => {
    try {
        const response = await axiosInstance.get<any>("/api/company-requests/businesses", { params: filters });
        return Array.isArray(response.data) ? response.data : response.data.data || [];
    } catch (error: unknown) {
        logger.error("Error fetching businesses:", extractErrorMessage(error));
        return [];
    }
};

export const reassessAll = async (): Promise<{ jobId: string }> => {
    try {
        const response = await axiosInstance.post<{ jobId: string }>("/api/company-requests/reassess-all");
        return response.data;
    } catch (error: unknown) {
        logger.error("Error starting reassessment:", extractErrorMessage(error));
        throw error;
    }
};

export const reassessRequest = async (requestId: number): Promise<{ jobId: string }> => {
    try {
        const response = await axiosInstance.post<{ jobId: string }>(`/api/company-requests/${requestId}/reassess`);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error starting reassessment for request ${requestId}:`, extractErrorMessage(error));
        throw error;
    }
};
